import { ChevronRight, Trophy } from 'lucide-react';
import { Link, useLocation } from 'wouter';
import { trpc } from '@/lib/trpc';

/**
 * Dashboard-only challenge discovery for authenticated clients. Challenge HQ
 * remains easy to reach without taking permanent space from training,
 * community, nutrition, messaging, or other focused screens.
 */
export function ActiveChallengeBanner() {
  const [location] = useLocation();
  const isToday = location === '/client' || location === '/client/';
  const { data: joined = [] } = trpc.challenges.myJoined.useQuery(undefined, {
    enabled: isToday,
    staleTime: 60_000,
  });
  const { data: programEnrollments = [] } = trpc.challengePrograms.myEnrollments.useQuery(undefined, {
    enabled: isToday,
    staleTime: 60_000,
  });

  if (!isToday) return null;

  const programEnrollment = (programEnrollments as any[])[0] ?? null;
  const basicChallenge = (joined as any[])[0] ?? null;
  const total = (programEnrollments as any[]).length + (joined as any[]).length;
  if (!programEnrollment && !basicChallenge) return null;

  const href = programEnrollment?.enrollment?.id
    ? `/client/challenge-program/${programEnrollment.enrollment.id}`
    : `/client/challenges/${basicChallenge.id}`;
  const name = programEnrollment?.program?.name ?? basicChallenge?.name ?? 'Active Challenge';
  const current = Number(basicChallenge?.currentValue ?? 0);
  const target = Number(basicChallenge?.targetValue ?? 0);
  const detail = programEnrollment
    ? 'Your challenge command room is active.'
    : target > 0
      ? `${current.toLocaleString()} / ${target.toLocaleString()} ${basicChallenge?.metric ?? ''}`.trim()
      : 'Your challenge room, progress, and leaderboard.';

  return (
    <Link href={href} aria-label={`Open active challenge: ${name}`}>
      <div
        data-active-challenge-banner
        data-contrast="dark"
        className="premium-dark-surface mx-3 mt-2 flex min-h-11 items-center gap-2.5 rounded-xl px-3 py-2 active:scale-[0.99] transition-all md:mx-auto md:max-w-[760px]"
        style={{
          background: 'linear-gradient(135deg, #231f19 0%, #17252c 100%)',
          border: '1px solid rgba(216,189,130,0.58)',
          boxShadow: '0 10px 28px rgba(23,21,21,0.22)',
        }}
      >
        <div className="grid size-8 shrink-0 place-items-center rounded-lg" style={{ background: 'rgba(216,189,130,0.16)', color: '#f0d49a' }}>
          <Trophy className="size-4" />
        </div>
        <div className="flex min-w-0 flex-1 items-center gap-2">
          <p className="shrink-0 text-[9px] font-black uppercase tracking-[0.18em]" style={{ color: '#f0d49a', fontFamily: 'Barlow Condensed, sans-serif' }}>
            Challenge
          </p>
          <span aria-hidden="true" className="h-3 w-px shrink-0 bg-white/20" />
          <div className="min-w-0 flex-1">
            <p className="truncate text-[13px] font-black uppercase leading-tight text-white" style={{ fontFamily: 'Barlow Condensed, sans-serif', letterSpacing: '0.035em' }}>{name}</p>
            <p className="truncate text-[9px] leading-tight" style={{ color: '#d7d9d6' }}>{detail}</p>
          </div>
          <div className="flex items-center gap-1.5">
            {total > 1 && (
              <span className="rounded-full px-1.5 py-0.5 text-[8px] font-black" style={{ background: 'rgba(255,255,255,0.12)', color: '#f7f5ef' }}>
                {total}
              </span>
            )}
          </div>
        </div>
        <ChevronRight className="size-4 shrink-0" style={{ color: '#f0d49a' }} />
      </div>
    </Link>
  );
}
